import { useRef, useState } from 'react';
import { Play, RotateCcw, Upload, X, AlertCircle } from 'lucide-react';
import {
  SOUND_TRIGGERS,
  SOUND_TRIGGER_LABELS,
  MAX_SOUND_BYTES,
  customSounds,
  setCustomSound,
  clearCustomSound,
  previewSound,
} from '../lib/sound.js';

const KB = (bytes) => `${Math.round(bytes / 1024)} KB`;

/**
 * Replacing the built-in tones with files of your own.
 *
 * Reached from the alert menu, because that is where an agent already goes to
 * decide how loud this console is. Everything here is per browser — see the
 * note in lib/sound.js — so the dialog is careful to say so rather than
 * letting someone assume their air-horn follows them to another machine.
 */
export function SoundSettingsDialog({ onClose }) {
  const [sounds, setSounds] = useState(customSounds);
  const [error, setError] = useState(null);
  // One hidden input per trigger, so a pick lands on the row that asked.
  const pickers = useRef({});

  const choose = async (trigger, file) => {
    if (!file) return;
    setError(null);
    try {
      await setCustomSound(trigger, file);
      setSounds(customSounds());
    } catch (err) {
      setError(err.message);
    }
  };

  const reset = (trigger) => {
    clearCustomSound(trigger);
    setSounds(customSounds());
  };

  return (
    <div className="dialog-backdrop" onMouseDown={onClose}>
      <div
        className="dialog"
        role="dialog"
        aria-modal="true"
        aria-label="Notification sounds"
        onMouseDown={(e) => e.stopPropagation()}
      >
        <div className="dialog__head">
          <h2 className="dialog__title">Notification sounds</h2>
          <button type="button" className="icon-button" onClick={onClose} aria-label="Close">
            <X size={14} />
          </button>
        </div>

        <p className="dialog__lede">
          Upload a short audio file for any of these, or leave it on the built-in tone. Stored
          in this browser only, up to {KB(MAX_SOUND_BYTES)} each.
        </p>

        <div className="settings">
          {SOUND_TRIGGERS.map((trigger) => {
            const custom = Boolean(sounds[trigger]);
            return (
              <div className="settings__row" key={trigger}>
                <span>
                  {SOUND_TRIGGER_LABELS[trigger]}
                  <span className="muted"> — {custom ? 'your file' : 'built-in tone'}</span>
                </span>
                <span className="settings__control">
                  <button
                    type="button"
                    className="button"
                    onClick={() => previewSound(trigger)}
                    title="Play it"
                  >
                    <Play size={12} /> Preview
                  </button>
                  <button
                    type="button"
                    className="button"
                    onClick={() => pickers.current[trigger]?.click()}
                  >
                    <Upload size={12} /> {custom ? 'Replace' : 'Upload'}
                  </button>
                  {custom && (
                    <button
                      type="button"
                      className="button button--ghost"
                      onClick={() => reset(trigger)}
                      title="Back to the built-in tone"
                    >
                      <RotateCcw size={12} /> Reset
                    </button>
                  )}
                  <input
                    ref={(el) => {
                      pickers.current[trigger] = el;
                    }}
                    type="file"
                    accept="audio/*"
                    hidden
                    onChange={(e) => {
                      choose(trigger, e.target.files?.[0]);
                      // Lets the same file be picked twice in a row after a
                      // rejection — otherwise the input reports no change.
                      e.target.value = '';
                    }}
                  />
                </span>
              </div>
            );
          })}
        </div>

        {error && (
          <div className="result result--error">
            <AlertCircle size={13} /> {error}
          </div>
        )}
      </div>
    </div>
  );
}

export default SoundSettingsDialog;
